import { createClient } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const SUPABASE_SINGLETON_KEY = '__june_supabase_client__';

// Rate-limit observability (Sprint 2 Item 2).
// Supabase has no native webhook for 429s. This fetch wrapper forwards every
// 429 response to Sentry so we can alert-rule on rate-limit abuse / misconfig.
// Throttled: we don't want a badly-rate-limited user to flood Sentry with 100+
// events. Max 5 events per endpoint per 60s per session.
const _rateLimitThrottle = new Map(); // endpoint → { count, windowStart }
const THROTTLE_WINDOW_MS = 60_000;
const THROTTLE_MAX_EVENTS = 5;

function shouldReport(endpoint) {
  const now = Date.now();
  const entry = _rateLimitThrottle.get(endpoint);
  if (!entry || now - entry.windowStart > THROTTLE_WINDOW_MS) {
    _rateLimitThrottle.set(endpoint, { count: 1, windowStart: now });
    return true;
  }
  entry.count += 1;
  return entry.count <= THROTTLE_MAX_EVENTS;
}

async function supabaseFetch(input, init) {
  const response = await fetch(input, init);
  if (response.status === 429) {
    try {
      const url = typeof input === 'string' ? input : input?.url || '';
      // Categorize endpoint for Sentry grouping
      let endpoint = 'unknown';
      if (url.includes('/auth/v1/signup')) endpoint = 'auth/signup';
      else if (url.includes('/auth/v1/token')) endpoint = 'auth/token';
      else if (url.includes('/auth/v1/')) endpoint = 'auth/other';
      else if (url.includes('/rest/v1/rpc/')) endpoint = 'rpc';
      else if (url.includes('/rest/v1/')) endpoint = 'rest';

      if (shouldReport(endpoint)) {
        // Peek at response body to capture Supabase's error_code without
        // consuming it (clone so the original caller still gets the full body).
        let errorCode = null;
        try {
          errorCode = response.clone().headers.get('x-sb-error-code');
        } catch { /* noop */ }

        // Dynamic import keeps this out of the initial module graph for zero
        // startup cost on cold-starts that never hit 429.
        import('./sentry').then(({ captureError }) => {
          const err = new Error(`Supabase rate limit exceeded: ${endpoint} (${errorCode || 'unknown'})`);
          err.name = 'RateLimitError';
          captureError?.(err, {
            category: 'rate_limit',
            endpoint,
            error_code: errorCode,
            status: 429,
            url: url.replace(/[\w-]{20,}/g, '<redacted>'), // never leak tokens/ids
          });
        }).catch(() => { /* never throw from observability */ });
      }
    } catch { /* never throw from observability */ }
  }
  return response;
}

function getSupabaseClient() {
  const globalScope = typeof globalThis !== 'undefined' ? globalThis : window;
  if (!globalScope[SUPABASE_SINGLETON_KEY]) {
    const client = createClient(supabaseUrl, supabaseAnonKey, {
      global: { fetch: supabaseFetch },
    });

    // Sentry auto-instrumentation for every PostgREST / RPC / auth / storage call.
    // This replaces the 2023-era pattern of manually wrapping each call in
    // safeQuery/safeMutation for observability. The integration creates spans,
    // breadcrumbs, and auto-captures errors at the client-method layer.
    //
    // safeQuery/safeMutation are PRESERVED for their separate concern:
    // user-facing UX (toast notifications, sanitized error messages). See
    // docs/runbooks/auth-rate-limits.md §5 for the full two-layer observability
    // model (fetch wrapper for 429s + supabaseIntegration for everything else).
    //
    // No-op if Sentry isn't initialized (dev without DSN) — the integration
    // function simply wraps client methods; if no events flow to Sentry,
    // there's no runtime cost beyond one method-wrap per call.
    try {
      if (typeof Sentry.instrumentSupabaseClient === 'function') {
        Sentry.instrumentSupabaseClient(client);
      }
    } catch (err) {
      // Never let instrumentation break the client — observability is
      // fire-and-forget.
      if (typeof console !== 'undefined') {
        console.warn('[Sentry] Supabase instrumentation failed (non-blocking):', err?.message || err);
      }
    }

    globalScope[SUPABASE_SINGLETON_KEY] = client;
  }
  return globalScope[SUPABASE_SINGLETON_KEY];
}

export const supabase = getSupabaseClient();

// Fallback user ID is opt-in for local prototyping only.
// In production, and in normal dev hardening, null forces proper auth handling.
const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV;
const devFallbackEnabled = isDev && import.meta.env?.VITE_ENABLE_DEV_AUTH_FALLBACK === 'true';
export const FALLBACK_USER_ID = devFallbackEnabled ? '00000000-0000-0000-0000-000000000001' : null;

// Legacy alias — will be removed once all imports are migrated
export const CURRENT_USER_ID = FALLBACK_USER_ID;

/**
 * Get the current authenticated user's ID.
 * Returns null in production if not authenticated (no silent fallback).
 */
export async function getCurrentUserId() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || FALLBACK_USER_ID;
  } catch {
    return FALLBACK_USER_ID;
  }
}

// Global abort controller for cancelling in-flight requests on session expiry
let _abortController = new AbortController();
export function getAbortSignal() { return _abortController.signal; }
export function abortAllRequests() {
  _abortController.abort();
  _abortController = new AbortController();
}

// Module-level cache for the auth user ID — set by AuthContext's onAuthStateChange listener
let _cachedAuthUserId = null;

export function setAuthUserId(id) { _cachedAuthUserId = id; }
export function getAuthUserId() { return _cachedAuthUserId || FALLBACK_USER_ID; }
export function requireAuthUserId(action = 'This action') {
  const userId = getAuthUserId();
  if (!userId) {
    throw new Error(`${action} requires an authenticated user.`);
  }
  return userId;
}
